import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Armchair, ScanSearch, Network as NetworkIcon,
  Cpu, Settings as SettingsIcon, LogOut, ShieldCheck, RefreshCw, Plug, Home, Bell, Palette, UserCircle, Image as ImageIcon, MapPin, FileText, X, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";

const BG_PRESETS = [
  { key: "slate", label: "Slate", color: "#1E2A3E" },
  { key: "steel", label: "Steel", color: "#2B3A52" },
  { key: "denim", label: "Denim", color: "#2E3D63" },
  { key: "plum", label: "Plum", color: "#3A2C52" },
  { key: "pine", label: "Pine", color: "#24413A" },
  { key: "graphite", label: "Graphite", color: "#3A4152" },
];

const BG_IMAGES = [
  { key: "smoky", label: "Barbershop", url: "https://images.unsplash.com/photo-1536520002442-39764a41e987?crop=entropy&cs=srgb&fm=jpg&q=88&w=2400" },
  { key: "brick", label: "Brick", url: "https://images.unsplash.com/photo-1675599193990-33d71150902b?crop=entropy&cs=srgb&fm=jpg&q=88&w=2400" },
  { key: "modern", label: "Salon", url: "https://images.unsplash.com/photo-1626383137804-ff908d2753a2?crop=entropy&cs=srgb&fm=jpg&q=88&w=2400" },
  { key: "luxe", label: "Luxe", url: "https://images.unsplash.com/photo-1706629505300-168aa1604912?crop=entropy&cs=srgb&fm=jpg&q=88&w=2400" },
  { key: "boutique", label: "Boutique", url: "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&q=88&w=2400" },
  { key: "spa", label: "Spa", url: "https://images.unsplash.com/photo-1693578538512-fc66f318c833?crop=entropy&cs=srgb&fm=jpg&q=88&w=2400" },
];

const NAV = [
  { to: "/documents", label: "Documents", icon: FileText, corporate: true, testid: "nav-documents" },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/floor", label: "Live Floor View", icon: Armchair, testid: "nav-floor" },
  { to: "/reconciliation", label: "Reconciliation", icon: ScanSearch, testid: "nav-reconciliation" },
  { to: "/network", label: "Network Rollup", icon: NetworkIcon, corporate: true, testid: "nav-network" },
  { to: "/locations", label: "Locations", icon: MapPin, testid: "nav-locations" },
  { to: "/devices", label: "Device Health", icon: Cpu, testid: "nav-devices" },
  { to: "/servicing", label: "Servicing", icon: CalendarClock, testid: "nav-servicing" },
  { to: "/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts" },
  { to: "/integrations", label: "Integrations", icon: Plug, corporate: true, testid: "nav-integrations" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/profile", label: "Profile", icon: UserCircle, testid: "nav-profile" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const isCorporate = user?.role === "corporate";
  const onDashboard = location.pathname === "/";

  // Reset scroll to the top on every route change so a new page always opens
  // at its introduction rather than inheriting the previous page's scroll.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  const { data: alertData } = useQuery({
    queryKey: ["alerts"],
    queryFn: () => api.get("/alerts").then((r) => r.data),
    refetchInterval: 30000,
  });
  const unread = alertData?.unread || 0;

  const [bgOpen, setBgOpen] = useState(false);
  const [bg, setBg] = useState(() => localStorage.getItem("app-bg") || "#1E2A3E");
  useEffect(() => {
    document.documentElement.style.setProperty("--app", bg);
    localStorage.setItem("app-bg", bg);
  }, [bg]);

  const [bgImage, setBgImage] = useState(() => {
    const v = localStorage.getItem("app-bg-image");
    return v && v !== "off" ? v : "";
  });
  const [bgUploading, setBgUploading] = useState(false);
  // Persist the chosen workspace background: per-browser always, and globally
  // (server-side) when a corporate admin picks it, so it shows on every device.
  const chooseBg = (url) => {
    setBgImage(url || "");
    localStorage.setItem("app-bg-image", url || "off");
    if (isCorporate) api.put("/branding/app-bg", { app_bg_image: url || "" }).catch(() => {});
    toast.success(url ? "Backdrop photo updated" : "Backdrop photo turned off");
    setBgOpen(false);
  };
  // If this browser has never chosen, adopt the server-side default.
  useEffect(() => {
    if (localStorage.getItem("app-bg-image") === null && branding?.app_bg_image) {
      setBgImage(branding.app_bg_image);
    }
  }, [branding?.app_bg_image]);
  const handleBgUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      chooseBg(data.url);
      toast.success("Background photo set");
    } catch { toast.error("Upload failed — use a JPG/PNG/WEBP under 6MB"); }
    setBgUploading(false);
    e.target.value = "";
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const reseed = async () => {
    try {
      await api.post("/demo/reseed");
      toast.success("Demo data regenerated");
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error("Could not reseed data");
    }
  };

  return (
    <div
      className="min-h-screen bg-[var(--app)] text-white flex bg-cover bg-center md:bg-fixed"
      style={bgImage ? { backgroundImage: `linear-gradient(rgba(12,16,26,0.40), rgba(12,16,26,0.66)), url(${bgImage})` } : undefined}
    >
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#2A3550] bg-[#171E2E] sticky top-0 h-screen z-10">
        <div className="px-6 py-6 border-b border-[#2A3550]">
          <div className="flex items-center gap-3">
            <div
              className="relative h-10 w-10 rounded-xl flex items-center justify-center ring-1 ring-white/10 shadow-lg shadow-[#6366F1]/20"
              style={{ background: "linear-gradient(135deg,#6366F1 0%,#38BDF8 100%)" }}
            >
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-[15px] tracking-tight text-white">
                {branding.logo_text}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#38BDF8] font-semibold mt-0.5">
                Revenue Assurance
              </div>
            </div>
          </div>
          <div className="mt-3.5 pt-3 border-t border-[#2A3550]">
            <div className="text-[9px] uppercase tracking-[0.16em] text-slate-500">A division of</div>
            <div className="text-[11px] font-medium text-slate-300 tracking-tight leading-snug mt-0.5">
              Welcome to the Colosseum Inc.
            </div>
          </div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
          {NAV.filter((n) => !n.corporate || isCorporate).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-[var(--brand)] text-white"
                    : "text-slate-400 hover:text-white hover:bg-[#171E2E]"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-4 space-y-1">
          {isCorporate && (
            <button
              onClick={reseed}
              data-testid="reseed-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-[#171E2E] transition-colors duration-200"
            >
              <RefreshCw className="h-[18px] w-[18px]" />
              Regenerate Demo
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0E131F]/80 border-b border-[#2A3550]">
          <div className="flex items-center justify-between px-5 md:px-8 h-16">
            <div className="md:hidden leading-tight">
              <div className="font-display font-semibold text-sm text-white">{branding.logo_text}</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#38BDF8] font-semibold">Revenue Assurance</div>
              <div className="text-[8px] uppercase tracking-[0.12em] text-slate-500">A division of Welcome to the Colosseum Inc.</div>
            </div>
            <div className="hidden md:block">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
                {branding.company_name} · Network Control
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  onClick={() => setBgOpen((o) => !o)}
                  data-testid="bg-picker-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#2A3550] bg-[#1E2638] px-3 py-2 text-sm text-slate-300 hover:text-white hover:border-[#38BDF8] transition-colors duration-200"
                  title="Change background theme"
                >
                  <Palette className="h-[18px] w-[18px]" />
                  <span className="hidden sm:inline">Theme</span>
                  <span className="h-3.5 w-3.5 rounded-full border border-[#334063]" style={{ backgroundColor: bg }} />
                </button>
                {bgOpen && (
                  <div
                    className="absolute right-0 mt-2 p-3 rounded-xl border border-[#2A3550] bg-[#171E2E] shadow-2xl z-30 w-56"
                    data-testid="bg-picker-menu"
                    onMouseLeave={() => setBgOpen(false)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-medium text-slate-200">Theme</div>
                      <button
                        onClick={() => setBgOpen(false)}
                        data-testid="bg-picker-close"
                        title="Close"
                        className="inline-flex items-center justify-center rounded-md p-1 text-slate-400 hover:text-white hover:bg-[#232C40] transition-colors duration-150"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-2">Background</div>
                    <div className="flex flex-wrap gap-2.5">
                      {BG_PRESETS.map((p) => (
                        <button
                          key={p.key}
                          onClick={() => setBg(p.color)}
                          title={p.label}
                          data-testid={`bg-${p.key}`}
                          className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-110"
                          style={{ backgroundColor: p.color, borderColor: bg === p.color ? "#38BDF8" : "#334063" }}
                        />
                      ))}
                    </div>
                    <label className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-[#2A3550] bg-[#1E2638] px-3 py-2 cursor-pointer">
                      <span className="text-xs text-slate-300">Custom color</span>
                      <input
                        type="color"
                        value={bg}
                        onChange={(e) => setBg(e.target.value)}
                        data-testid="bg-custom-color"
                        className="h-6 w-8 cursor-pointer rounded bg-transparent border-0 p-0"
                      />
                    </label>

                    <div className="mt-4 text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-2">Background photo</div>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => chooseBg("")}
                        data-testid="bg-photo-none"
                        className={`h-12 rounded-lg border-2 text-[11px] font-medium flex items-center justify-center bg-[#1E2638] transition-colors ${bgImage ? "border-[#334063] text-slate-400" : "border-[#38BDF8] text-white"}`}
                      >
                        Off
                      </button>
                      {branding.login_image && (
                        <button
                          onClick={() => chooseBg(branding.login_image)}
                          title="Your front photo"
                          data-testid="bg-photo-front"
                          className={`relative h-12 rounded-lg overflow-hidden border-2 transition-colors ${bgImage === branding.login_image ? "border-[#38BDF8]" : "border-transparent hover:border-[#334063]"}`}
                        >
                          <img src={branding.login_image} alt="front" className="h-full w-full object-cover" />
                        </button>
                      )}
                      {BG_IMAGES.map((im) => (
                        <button
                          key={im.key}
                          onClick={() => chooseBg(im.url)}
                          title={im.label}
                          data-testid={`bg-photo-${im.key}`}
                          className={`relative h-12 rounded-lg overflow-hidden border-2 transition-colors ${bgImage === im.url ? "border-[#38BDF8]" : "border-transparent hover:border-[#334063]"}`}
                        >
                          <img src={im.url} alt={im.label} className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                    <label className="mt-2 flex items-center justify-center gap-2 rounded-lg border border-[#2A3550] bg-[#1E2638] px-3 py-2 cursor-pointer text-xs text-slate-300 hover:border-[#38BDF8] transition-colors">
                      <ImageIcon className="h-4 w-4" /> {bgUploading ? "Uploading…" : "Upload your own"}
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" data-testid="bg-photo-upload" onChange={handleBgUpload} />
                    </label>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate("/alerts")}
                data-testid="header-alerts-btn"
                className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#232C40] transition-colors duration-200"
                title="Alerts"
              >
                <Bell className="h-[18px] w-[18px]" />
                {unread > 0 && (
                  <span
                    data-testid="alerts-unread-badge"
                    className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full text-[10px] font-semibold text-white flex items-center justify-center"
                    style={{ backgroundColor: "var(--brand)" }}
                  >
                    {unread}
                  </span>
                )}
              </button>
              <div className="text-right leading-tight hidden sm:block">
                <div className="text-sm font-medium" data-testid="current-user-name">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.15em]" style={{ color: "var(--brand)" }}>
                  {user?.role === "corporate" ? "Corporate · HQ" : user?.role === "manager" ? "Manager" : "Store Owner"}
                </div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1560250097-0b93528c311a"
                alt="avatar"
                className="h-9 w-9 rounded-full object-cover border border-[#334063]"
              />
              <button
                onClick={doLogout}
                data-testid="logout-btn"
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#232C40] transition-colors duration-200"
                title="Sign out"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
          {/* mobile nav */}
          <nav className="md:hidden flex overflow-x-auto gap-1 px-3 pb-2">
            {NAV.filter((n) => !n.corporate || isCorporate).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 rounded-lg text-xs transition-colors duration-200 ${
                    isActive ? "bg-[var(--brand)] text-white" : "text-slate-400"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </header>

        <main className="flex-1 px-5 md:px-8 py-8 max-w-[1500px] w-full mx-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Floating back-to-dashboard button */}
      {!onDashboard && (
        <button
          onClick={() => navigate("/")}
          data-testid="floating-dashboard-btn"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-white shadow-xl shadow-black/40 hover:-translate-y-0.5 transition-transform duration-200"
          style={{ backgroundColor: "var(--brand)" }}
          title="Back to dashboard"
        >
          <Home className="h-[18px] w-[18px]" />
          Dashboard
        </button>
      )}
    </div>
  );
}
